import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, AlertCircle, Check, Archive } from 'lucide-react';
import { api } from '../../api/client';
import type {
  CustomerUsage, LineItemTypeUsage, TaxLabelUsage,
  QboCustomerSummary, QboItemSummary, QboTaxCodeSummary, QboAccountSummary,
  StoredMapping, QboBinding,
} from '../../types/qbo-mapping';
import QboRateSheetItemMapping from './QboRateSheetItemMapping';
import QboReadinessCard from './QboReadinessCard';
import EwQboMapping from './EwQboMapping';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { qboLineItemTypeLabel } from '../../lib/qbo-line-item-label';

/**
 * BINDING FieldIQ CONCEPTS TO EXISTING QUICKBOOKS OBJECTS.
 *
 * Nothing on this screen creates anything in the tenant's books. A push refuses
 * with 409 `QBO_MAPPING_REQUIRED` when a binding is missing, carrying
 * `mappingKind` and `missingKeys` — and until this screen existed that refusal
 * was a dead end: it named a settings page that had never been built, so the
 * mappings were done by hand in SQL.
 *
 * Four kinds, one row shape: something of ours on the left, a dropdown of the
 * realm's objects on the right, saved on change.
 *
 *   customer  the customers this tenant invoices          (0393)
 *   rate_sheet_item  the rate-sheet rows lines are priced from (0406) —
 *             checked FIRST at push; its own component, because it needs bulk
 *             selection and cross-sheet suggestions the other kinds do not
 *   item      the line_item_types this tenant bills on
 *   tax_code  the (jurisdiction, label) pairs it charges
 *   discount  one account per realm; resolves itself when unique
 *
 * ── ARCHIVED CANDIDATES ARE SHOWN, NOT HIDDEN ──────────────────────────────
 * The API returns archived QuickBooks objects flagged rather than filtered, and
 * this screen renders them that way: labelled, and after the live ones. Hiding
 * them would swap a silent wrong answer for a silent absence — an operator who
 * can see the customer in QuickBooks would find nothing here and conclude the
 * integration is broken. Same reasoning as the resolver's.
 *
 * ⚠️ THIS BEHAVIOUR HAS NO PRODUCTION INSTANCE. Verified in the browser against
 * Smith's realm 1279814200 on 2026-09-10: it holds ZERO archived customers, so
 * the flag-and-sort-last path is exercised ONLY by the unit test. It is
 * asserted, not observed. Whoever next opens this screen on a realm that has
 * archived objects should check it and replace this paragraph with what they
 * saw.
 */

type Kind = 'customer' | 'rate_sheet_item' | 'item' | 'tax_code' | 'discount_account' | 'earthworks';

const TABS: { kind: Kind; label: string; blurb: string }[] = [
  { kind: 'customer', label: 'Customers', blurb: 'Which QuickBooks customer each of yours is. FieldIQ never creates one or matches by name — a near-miss makes a duplicate that then collects every future invoice.' },
  { kind: 'rate_sheet_item', label: 'Rate sheet rows', blurb: 'The QuickBooks item for each service on your rate sheets. A line priced from a row posts to that row\'s item — this is how casing tong and integral tong reach different items. Power tongs lines are mapped here only.' },
  { kind: 'item',     label: 'Line items', blurb: 'The fallback for a line with no mapped rate sheet row. QuickBooks requires an item on every invoice line; an unmapped type refuses the push rather than posting to the wrong income account.' },
  { kind: 'tax_code', label: 'Tax codes',  blurb: 'One code per province and label. Rate alone is ambiguous and the code names are often single letters, so this cannot be derived.' },
  { kind: 'discount_account', label: 'Discounts', blurb: 'The account a discount line posts to. Resolves itself when your chart of accounts has exactly one — bind it here only to override that.' },
];

/** S10: earthworks' own items, tax codes and customers. Shown only to a tenant with the vertical. */
const EARTHWORKS_TAB = { kind: 'earthworks' as const, label: 'Earthworks', blurb: 'What an earthworks invoice posts to. Its lines use their own item keys, so nothing here changes how a casing invoice posts.' };

/** One line of the table: something of ours, and what it is bound to. */
interface Row { key: string; label: string; sublabel?: string; count?: number; binding: QboBinding | null; rowMappedOnly?: boolean }
/** One option in the dropdown: something of theirs. */
interface Candidate { id: string; label: string; active: boolean; hint?: string }

/**
 * `'data' in body ? body.data : body`, never `body?.data || body`.
 *
 * The `||` form falls through to the ENVELOPE when the API sends
 * `{success: true, data: null}` — the object is truthy, every field then reads
 * `undefined`, and the screen renders blanks that look like data. 62 call
 * sites in this repo carry that bug; this is not going to be 63.
 */
function unwrap<T>(body: { data?: T } | T | undefined): T | undefined {
  if (body && typeof body === 'object' && 'data' in body) return (body as { data?: T }).data;
  return body as T | undefined;
}

export default function QuickBooksMappingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Optional: the screen's own tests render it with no AuthProvider, and no
  // provider means no vertical, so no Earthworks tab.
  const earthworks = useOptionalAuth()?.hasVertical('earthworks') === true;
  const tabs = useMemo(() => (earthworks ? [...TABS, EARTHWORKS_TAB] : TABS), [earthworks]);

  // The push refusal deep-links here with ?kind=customer, so the office lands
  // on the tab that blocked them instead of hunting for it.
  const requested = searchParams.get('kind') as Kind | null;
  const [tab, setTab] = useState<Kind>(
    requested && tabs.some((t) => t.kind === requested) ? requested : 'customer'
  );
  const [toast, setToast] = useState<{ text: string; tone: 'success' | 'error' } | null>(null);

  const notify = (text: string, tone: 'success' | 'error' = 'success') => {
    setToast({ text, tone });
    setTimeout(() => setToast(null), 3500);
  };

  // ── What needs mapping, per kind ──────────────────────────────────────────
  const needs = useQuery({
    queryKey: ['qbo', 'needs', tab],
    enabled: tab !== 'rate_sheet_item' && tab !== 'earthworks',
    queryFn: async () => {
      if (tab === 'customer') {
        const rows = unwrap((await api.quickbooks.mappings.customersInUse()).data) ?? [];
        return rows.map((r: CustomerUsage): Row => ({
          key: r.customerId, label: r.customerName, count: r.invoiceCount, binding: r.binding,
        }));
      }
      if (tab === 'item') {
        const rows = unwrap((await api.quickbooks.mappings.lineItemTypesInUse()).data) ?? [];
        return rows.map((r: LineItemTypeUsage): Row => ({
          key: r.lineItemType, label: qboLineItemTypeLabel(r.lineItemType), count: r.invoiceCount, binding: r.binding,
          rowMappedOnly: r.requiresRowMapping === true,
        }));
      }
      if (tab === 'tax_code') {
        const rows = unwrap((await api.quickbooks.mappings.taxLabelsInUse()).data) ?? [];
        return rows.map((r: TaxLabelUsage): Row => ({
          key: r.localKey, label: r.label, sublabel: r.jurisdiction, count: r.invoiceCount, binding: r.binding,
        }));
      }
      // discount_account is per-realm: one row, empty key, and the binding
      // comes from the general mappings list.
      const all = unwrap((await api.quickbooks.mappings.list()).data) ?? [];
      const pinned = all.find((m: StoredMapping) => m.kind === 'discount_account');
      return [{
        key: '', label: 'Discount account',
        binding: pinned ? { qboId: pinned.qboId, qboName: pinned.qboName } : null,
      }] as Row[];
    },
  });

  // ── The realm's own objects to pick from ──────────────────────────────────
  const candidates = useQuery({
    queryKey: ['qbo', 'candidates', tab],
    enabled: tab !== 'rate_sheet_item' && tab !== 'earthworks',
    queryFn: async (): Promise<Candidate[]> => {
      if (tab === 'customer') {
        const rows = unwrap((await api.quickbooks.mappings.realmCustomers()).data) ?? [];
        return rows.map((r: QboCustomerSummary) => ({ id: r.id, label: r.displayName, active: r.active, hint: r.primaryEmail ?? undefined }));
      }
      if (tab === 'item') {
        const rows = unwrap((await api.quickbooks.mappings.realmItems()).data) ?? [];
        return rows.map((r: QboItemSummary) => ({ id: r.id, label: r.fullyQualifiedName || r.name, active: r.active, hint: r.incomeAccountName ?? undefined }));
      }
      if (tab === 'tax_code') {
        const rows = unwrap((await api.quickbooks.mappings.realmTaxCodes()).data) ?? [];
        return rows.map((r: QboTaxCodeSummary) => ({ id: r.id, label: r.name, active: r.active !== false, hint: r.description ?? undefined }));
      }
      const rows = unwrap((await api.quickbooks.mappings.realmDiscountAccounts()).data) ?? [];
      return rows.map((r: QboAccountSummary) => ({ id: r.id, label: r.name, active: r.active !== false }));
    },
  });

  const save = useMutation({
    mutationFn: (v: { key: string; qboId: string }) =>
      api.quickbooks.mappings.save({ kind: tab, local_key: v.key, qbo_id: v.qboId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qbo'] });
      notify('Mapping saved');
    },
    onError: (err: any) => notify(err?.response?.data?.error || 'Failed to save mapping', 'error'),
  });

  const remove = useMutation({
    mutationFn: (key: string) => api.quickbooks.mappings.remove(tab, key || '-'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qbo'] });
      notify('Mapping removed');
    },
    onError: (err: any) => notify(err?.response?.data?.error || 'Failed to remove mapping', 'error'),
  });

  const notConnected =
    (needs.error as any)?.response?.data?.code === 'QBO_NOT_CONNECTED' ||
    (candidates.error as any)?.response?.data?.code === 'QBO_NOT_CONNECTED';

  // Depend on the QUERY RESULTS, not on `x ?? []` expressions — a fresh array
  // literal every render defeats the memo and trips exhaustive-deps.
  const needsData = needs.data;
  const candidateData = candidates.data;
  const rows: Row[] = useMemo(() => needsData ?? [], [needsData]);
  const candidateRows: Candidate[] = useMemo(() => candidateData ?? [], [candidateData]);
  const unmapped = useMemo(() => rows.filter((r) => !r.binding && !r.rowMappedOnly).length, [rows]);
  const archivedCount = useMemo(() => candidateRows.filter((c) => !c.active).length, [candidateRows]);

  /**
   * THREE STATES, not two.
   *
   * A row can be unmapped, bound to something that exists in this realm, or
   * BOUND TO AN ID THIS REALM DOES NOT HOLD. The third is what a carried-over
   * binding looks like when the tenant is on a different QuickBooks company
   * than the one the id came from — 0345's failure, where seven carried invoice
   * ids turned out to point at Deposits.
   *
   * It was rendering as "— not mapped —" because a <select> whose `value`
   * matches no <option> falls back to the first one. Two parts of this screen
   * then disagreed on live data: the dropdown said unmapped, the status column
   * said mapped, and nothing said anything was wrong. Observed on Smith
   * 2026-09-10: bindings #58 and #59 are absent from realm 1279814200 entirely
   * — not archived and hidden, absent.
   *
   * The silent fallback is the defect. The missing customer is a fact the
   * operator needs told.
   */
  const candidateIds = useMemo(() => new Set(candidateRows.map((c) => c.id)), [candidateRows]);
  /** Only meaningful once the catalog has actually loaded — see `catalogReady`. */
  const catalogReady = candidates.isSuccess;
  const isStale = (r: Row) => catalogReady && !!r.binding && !candidateIds.has(r.binding.qboId);
  const staleCount = useMemo(
    () => (catalogReady ? rows.filter((r) => !!r.binding && !candidateIds.has(r.binding!.qboId)).length : 0),
    [catalogReady, rows, candidateIds],
  );
  const active = tabs.find((t) => t.kind === tab) ?? TABS[0];

  /* A DRILL-IN, SO IT KEEPS ITS ARROW AND GIVES UP ITS h1. The AI &
     integrations workspace now owns the one h1 on this surface, as every other
     workspace already did, so a second one here would be two page titles on one
     route. The arrow is NOT the redundant kind the tab pages shed: it is the
     only way back to the Integrations tab this screen was opened from, and it
     goes somewhere the band's arrow and the sidebar do not. */
  return (
    <div className="min-h-full">
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} aria-label="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-foreground">QuickBooks mapping</h2>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {/* Push readiness (API #887). Switches the tab in place and keeps the
            URL's ?kind= in step, so the link is shareable and needs no reload. */}
        <QboReadinessCard
          onSelectKind={(kind) => { setTab(kind); setSearchParams({ kind }); }}
        />

        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.kind}
              onClick={() => setTab(t.kind)}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                tab === t.kind
                  ? 'bg-fieldiq-accent text-fieldiq-dark font-semibold'
                  : 'bg-fieldiq-card border border-fieldiq-border text-white hover:border-fieldiq-accent'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-fieldiq-muted max-w-3xl">{active.blurb}</p>

        {notConnected ? (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 text-center">
            <p className="text-white">QuickBooks is not connected.</p>
            <button
              onClick={() => navigate('/settings/integrations')}
              className="mt-3 px-4 py-2 rounded-lg bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold"
            >
              Go to Integrations
            </button>
          </div>
        ) : tab === 'rate_sheet_item' ? (
          <QboRateSheetItemMapping notify={notify} />
        ) : tab === 'earthworks' ? (
          <EwQboMapping notify={notify} />
        ) : needs.isLoading || candidates.isLoading ? (
          <div className="flex items-center gap-2 text-fieldiq-muted text-sm">
            <Loader2 size={16} className="animate-spin" /> Loading from QuickBooks…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 text-xs text-fieldiq-muted">
              <span data-testid="unmapped-count">
                {unmapped === 0
                  ? 'Everything here is mapped'
                  : `${unmapped} of ${rows.length} still ${unmapped === 1 ? 'needs' : 'need'} a mapping`}
              </span>
              {/*
                Its OWN line, not folded into the unmapped count. A stale
                binding is not "still needs a mapping" — the operator already
                made a choice and it stopped resolving, which is a different
                thing to go and look at.
              */}
              {staleCount > 0 && (
                <span data-testid="stale-count" className="flex items-center gap-1 text-red-400">
                  <AlertCircle size={12} />
                  {staleCount} bound to {staleCount === 1 ? 'an object' : 'objects'} this QuickBooks company does not have
                </span>
              )}
              {archivedCount > 0 && (
                <span className="flex items-center gap-1">
                  <Archive size={12} />
                  {archivedCount} archived in QuickBooks, shown at the bottom of each list
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fieldiq-muted border-b border-fieldiq-border">
                    <th className="py-2 pr-4 font-medium">In FieldIQ</th>
                    <th className="py-2 pr-4 font-medium">On</th>
                    <th className="py-2 pr-4 font-medium">QuickBooks object</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-fieldiq-muted">Nothing here needs mapping yet.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.key || 'realm'} className="border-b border-fieldiq-border/50">
                      <td className="py-2 pr-4 text-white capitalize">
                        {r.label}
                        {r.sublabel && <span className="ml-2 text-xs text-fieldiq-muted">{r.sublabel}</span>}
                      </td>
                      <td className="py-2 pr-4 text-fieldiq-muted">
                        {/* Absent renders as —, never 0. */}
                        {r.count === undefined ? '—' : `${r.count} invoice${r.count === 1 ? '' : 's'}`}
                      </td>
                      <td className="py-2 pr-4">
                        {r.rowMappedOnly ? (
                          <button
                            onClick={() => setTab('rate_sheet_item')}
                            className="text-sm text-fieldiq-accent hover:underline text-left"
                          >
                            Mapped by rate sheet row — casing tong and integral tong are separate items
                          </button>
                        ) : (
                        <select
                          aria-label={`QuickBooks object for ${r.label}`}
                          value={r.binding?.qboId ?? ''}
                          disabled={save.isPending}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (!v) { remove.mutate(r.key); return; }
                            // Replacing an existing binding is as deliberate an
                            // act as creating one, and the operator is told what
                            // they are replacing. A stale binding especially —
                            // the row LOOKS unmapped, so the click that changes
                            // it does not feel like a change.
                            if (r.binding && r.binding.qboId !== v) {
                              const to = candidateRows.find((c) => c.id === v);
                              const from = isStale(r)
                                ? `#${r.binding.qboId}${r.binding.qboName ? ` (${r.binding.qboName})` : ''}, which is not in this QuickBooks company`
                                : `${r.binding.qboName ?? `#${r.binding.qboId}`}`;
                              if (!window.confirm(
                                `Replace the QuickBooks mapping for "${r.label}"?\n\n` +
                                `From: ${from}\nTo:   ${to?.label ?? `#${v}`}\n\n` +
                                `Future invoices for this customer will post against the new one.`
                              )) {
                                e.target.value = r.binding.qboId;   // put the control back
                                return;
                              }
                            }
                            save.mutate({ key: r.key, qboId: v });
                          }}
                          className={`bg-fieldiq-dark border rounded-lg px-2 py-1.5 text-white focus:border-fieldiq-accent focus:outline-none min-w-[16rem] ${
                            isStale(r) ? 'border-red-500' : 'border-fieldiq-border'
                          }`}
                        >
                          <option value="">— not mapped —</option>
                          {/*
                            THE STORED ID, RENDERED EXPLICITLY.
                            Without this option the <select> has no match for its
                            own value and shows the first entry instead — a
                            plausible substitute for a fact. Disabled, so it
                            cannot be re-selected as if it were a choice.
                          */}
                          {isStale(r) && r.binding && (
                            <option value={r.binding.qboId} disabled>
                              {`⚠ #${r.binding.qboId}${r.binding.qboName ? ` (${r.binding.qboName})` : ''} — not found in this QuickBooks company`}
                            </option>
                          )}
                          {candidateRows.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.label}
                              {c.active ? '' : '  (archived in QuickBooks)'}
                              {c.hint ? ` — ${c.hint}` : ''}
                            </option>
                          ))}
                        </select>
                        )}
                      </td>
                      <td className="py-2">
                        {r.rowMappedOnly
                          ? <span className="text-xs text-fieldiq-muted">per row</span>
                          : isStale(r)
                          ? (
                            <span className="flex items-center gap-1 text-red-400 text-xs" title={`Bound to QuickBooks id ${r.binding?.qboId}, which this company does not have.`}>
                              <AlertCircle size={13} /> not in this company
                            </span>
                          )
                          : r.binding
                            ? <span className="flex items-center gap-1 text-green-400 text-xs"><Check size={13} /> mapped</span>
                            : <span className="flex items-center gap-1 text-amber-400 text-xs"><AlertCircle size={13} /> blocks a push</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {toast && (
        <div
          role="status"
          className={`fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 z-50 text-white px-5 py-3 rounded-xl shadow-lg text-sm flex items-center gap-2 ${
            toast.tone === 'error' ? 'bg-red-600 border border-red-500' : 'bg-fieldiq-card border border-fieldiq-border'
          }`}
        >
          {toast.tone === 'error' && <AlertCircle size={16} />}
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
}
